import { Flex, Text } from '@chakra-ui/react';
import { FormFieldCheckboxArray } from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import DataAvailabilityStatementUpdateModal from './DataAvailabilityStatementUpdateModal';
import { FieldHeader } from '../common/FieldHeader';
import { CheckBoxArrayEmptyState } from '../CheckBoxArray/CheckBoxArrayEmptyState';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { CheckBoxArrayDiffModal } from '../CheckBoxArray/CheckBoxArrayDiffModal';
import { IFormFieldText } from '../Text';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import {
  useAuthorEditSubmission,
  useEditSubmissionMetadata,
  useSubmitRevision
} from 'src/api/commands';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@morressier/alembic';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';

type IProps = {
  formFieldDefinition: FormFieldCheckboxArray<'preference.dataAvailabilityStatement'>;
  value: string[];
  previousValue?: string[] | null;
  onChange?: (value: string[]) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

type OptionLabels = {
  main: string;
  secondary?: string;
};

export const DataAvailabilityStatementDisplay: React.FC<{
  value: string[];
  options: Record<string, OptionLabels>;
  dataAvailabilityTextValue?: string;
}> = ({ value, options, dataAvailabilityTextValue }) => {
  const selectedOptions = value
    .map(v => options[v])
    .filter((v): v is OptionLabels => Boolean(v));

  if (!selectedOptions.length && !dataAvailabilityTextValue) {
    return <CheckBoxArrayEmptyState />;
  }

  return (
    <Flex flexDirection="column" gap={2}>
      {selectedOptions.length > 0 &&
        selectedOptions.map(option => (
          <Text key={`${option.main}-${option.secondary}`} textStyle="textBody">
            • {option.main.replace(/\.$/, '')}
          </Text>
        ))}
      {dataAvailabilityTextValue && (
        <Text textStyle="textBody">
          <i>Additional Details: </i>
          {dataAvailabilityTextValue}
        </Text>
      )}
    </Flex>
  );
};

export const DataAvailabilityStatementField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated, getFieldState } =
    useSubmissionFields(entryPoint);
  const {
    submissionId,
    submissionContextId,
    organizationId,
    workflowResponse,
    fieldsResponse: { fields }
  } = useSubmissionData();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const submissionCurrentState = workflowResponse.workflowData
    .currentState as ISubmissionWFState;
  const command = fields['DataAvailabilityStatement']?.editing.isEditable
    ? fields['DataAvailabilityStatement'].editing.via
    : undefined;

  const submitRevisionMutation = useSubmitRevision({
    submissionId,
    submissionContextId,
    organizationId,
    articleWorkflowId: workflowResponse.articleWorkflow.id ?? '',
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  const editSubmissionMutation = useEditSubmissionMetadata({
    articleWorkflowId: workflowResponse.articleWorkflow.id ?? '',
    submissionContextId,
    submissionId,
    organizationId
  });

  const authorEditSubmissionMutation = useAuthorEditSubmission({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: workflowResponse.articleWorkflow.id ?? '',
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated =
    checkFormFieldValueIsUpdated(formFieldDefinition.name) ||
    checkFormFieldValueIsUpdated('DataAvailabilityText');

  const dataAvailabilityTextField = getFieldState('DataAvailabilityText');
  const dataAvailabilityTextConfig =
    dataAvailabilityTextField?.formFieldDefinition as
      | IFormFieldText
      | undefined;
  const dataAvailabilityTextValue =
    (dataAvailabilityTextField?.value as string) ?? '';
  const previousDataAvailabilityTextValue =
    (dataAvailabilityTextField as { previousValue?: string })?.previousValue ??
    null;
  const dataAvailabilityTextLabel =
    dataAvailabilityTextConfig?.params?.labels?.main;

  const handleSubmit = async (
    checkboxValues: string[],
    dataAvailabilityText?: string
  ) => {
    setIsEditing(false);

    const payload = {
      preference: {
        dataAvailabilityStatement: checkboxValues,
        ...(dataAvailabilityText !== undefined
          ? { dataAvailabilityText }
          : {})
      }
    };

    const response =
      submissionCurrentState === 'unsubmitted'
        ? await authorEditSubmissionMutation.mutateAsync(payload)
        : command === 'EditSubmission'
        ? await editSubmissionMutation.mutateAsync(payload)
        : await submitRevisionMutation.mutateAsync(payload);

    if (response?.isSuccess) {
      toast({
        variant: 'success',
        title: `${formFieldDefinition.params.labels.main} Updated`,
        isClosable: true
      });
      queryClient.invalidateQueries(['submission-fields', submissionId]);
    } else {
      toast({
        variant: 'error',
        title: 'Error updating field',
        description: 'There was an error updating the field'
      });
    }
  };

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <DataAvailabilityStatementUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          dataAvailabilityTextConfig={dataAvailabilityTextConfig}
          dataAvailabilityTextValue={dataAvailabilityTextValue}
          onSubmit={handleSubmit}
        />
      )}

      {isUpdated && showDiffModal ? (
        <CheckBoxArrayDiffModal
          fieldName={formFieldDefinition.params.labels.main}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
          options={formFieldDefinition.params.labels.options}
          dataAvailabilityTextValue={dataAvailabilityTextValue}
          previousDataAvailabilityTextValue={previousDataAvailabilityTextValue}
          dataAvailabilityTextLabel={dataAvailabilityTextLabel}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <DataAvailabilityStatementDisplay
          value={value}
          options={formFieldDefinition.params.labels.options}
          dataAvailabilityTextValue={dataAvailabilityTextValue}
        />
      ) : (
        <CheckBoxArrayEmptyState />
      )}
    </Flex>
  );
};
